import { useMemo, useRef, useState } from 'react'
import { Box, darken } from '@mui/material'
import { Table } from 'Common/MUITable/MUITable'
import RestoreIcon from '@mui/icons-material/Restore'
import {
  DataGridPremiumProps,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { autoHeight } from 'Common/utils/utils'
import {
  EditPlannedVolumeTableData,
  PlannedVolumeDataTableProps,
} from './PlannedVolume.types'
import { searchTextOperator } from 'Common/MUITable/SearchText.operator'
import { TCClosure } from 'Common/Components/MUITableToolbar'

export const PlannedVolumeDataTable = ({
  data,
  updateTableData,
  weeks,
  siteCodes,
  skuCodes,
}: PlannedVolumeDataTableProps) => {
  const unsavedChangesRef = useRef<{
    unsavedRows: Record<GridRowId, EditPlannedVolumeTableData>
    rowsBeforeChange: Record<GridRowId, EditPlannedVolumeTableData>
  }>({
    unsavedRows: {},
    rowsBeforeChange: {},
  })
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false)
  const apiRef = useGridApiRef()
  const [isSaving, setIsSaving] = useState(false)

  const saveChanges = () => {
    try {
      setHasUnsavedRows(false)
      apiRef.current.updateRows(
        Object.values(unsavedChangesRef.current.unsavedRows)
      )
      // Sending updated table data to parent component.
      const keysToUpdate = Object.keys(unsavedChangesRef.current.unsavedRows)
      if (keysToUpdate.length > 0) {
        updateTableData({ ...unsavedChangesRef.current.unsavedRows })
      }
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
      }
    } catch (error) {
      setIsSaving(false)
    }
  }

  const processRowUpdate: NonNullable<
    DataGridPremiumProps['processRowUpdate']
  > = (newRow, oldRow) => {
    const rowId = newRow.id
    const { replenishmentPlanArrivalAdjustment, replenishmentPlanArrival } =
      newRow
    const {
      replenishmentPlanArrivalAdjustment: oldReplenishmentPlanArrivalAdjustment,
    } = oldRow
    if (
      replenishmentPlanArrivalAdjustment !==
      oldReplenishmentPlanArrivalAdjustment
    ) {
      newRow['adjustedReplenishmentPlanArrival'] =
        replenishmentPlanArrivalAdjustment + replenishmentPlanArrival

      unsavedChangesRef.current.unsavedRows[rowId] = newRow
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow
      }
      setHasUnsavedRows(true)
    }
    return newRow
  }

  const replaceOptions = (arr: string[], column: string) => {
    return {
      type: 'singleSelect',
      valueOptions: arr,
      filterOperators: [
        {
          ...searchTextOperator[0],
          InputComponentProps: { data: arr, label: column },
        },
      ],
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              key={`#randomnkey${id}`}
              icon={<RestoreIcon />}
              label="Discard changes"
              disabled={unsavedChangesRef.current.unsavedRows[id] === undefined}
              onClick={() => {
                apiRef.current.updateRows([
                  unsavedChangesRef.current.rowsBeforeChange[id],
                ])
                delete unsavedChangesRef.current.rowsBeforeChange[id]
                delete unsavedChangesRef.current.unsavedRows[id]
                setHasUnsavedRows(
                  Object.keys(unsavedChangesRef.current.unsavedRows).length > 0
                )
              }}
            />,
          ]
        },
      },
      {
        headerName: 'Production date',
        field: 'formattedProductionDate',
        flex: 1,
        ...replaceOptions(weeks as string[], 'formattedDate'),
      },
      {
        headerName: 'Total planned hours',
        field: 'totalPlannedHours',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Work centre capacity',
        field: 'workcentreCapacity',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Over capacity hours',
        field: 'overCapacity',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Sku',
        field: 'skuCode',
        flex: 1,
        ...replaceOptions(skuCodes as string[], 'skuCode'),
      },
      {
        headerName: 'Cycle',
        field: 'cycleTime',
        flex: 1,
        type: 'number',
      },

      {
        headerName: 'MOQ / MOI',
        field: 'moqMoi',
        flex: 1,
      },
      {
        headerName: 'Initial production volume',
        field: 'productionPlannedVolume',
        flex: 1,
        type: 'number',
      },

      {
        headerName: 'Initial production hours',
        field: 'productionHours',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Replenishment site',
        field: 'siteCode',
        flex: 1,
        ...replaceOptions(siteCodes as string[], 'siteCode'),
      },
      {
        headerName: 'Arrival date',
        field: 'formattedDate',
        flex: 1,
        ...replaceOptions(weeks as string[], 'formattedDate'),
      },
      {
        headerName: 'Initial arrival quantity',
        field: 'replenishmentPlanArrival',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Adjusted arrival quantity',
        field: 'adjustedReplenishmentPlanArrival',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Arrival quantity adjustment',
        field: 'replenishmentPlanArrivalAdjustment',
        type: 'number',
        editable: true,
        flex: 1,
      },
      {
        headerName: 'Projected closing inventory',
        field: 'projectedClosingInventory',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Target closing inventory',
        field: 'targetCloseInventory',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Inventory excess',
        field: 'projectedVolumeDifference',
        type: 'number',
        flex: 1,
      },
    ],
    [data, apiRef]
  )

  const discardChanges = () => {
    setHasUnsavedRows(false)
    apiRef.current.updateRows(
      Object.values(unsavedChangesRef.current.rowsBeforeChange)
    )
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    }
  }
  const getRowId = (row: GridValidRowModel) => {
    return row.id
  }

  return (
    <Box>
      <Box
        id="#table-container-mui"
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
        }}
      >
        <Table
          getRowId={getRowId}
          sx={{
            '& .MuiDataGrid-row.row--edited': {
              backgroundColor: (theme) => {
                if (theme.palette.mode === 'light') {
                  return 'rgba(255, 254, 176, 0.3)'
                }
                return darken('rgba(255, 254, 176, 1)', 0.6)
              },
            },
          }}
          ref={autoHeight}
          apiRef={apiRef}
          rows={data ?? []}
          columns={columns}
          showCellVerticalBorder
          showColumnVerticalBorder
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          experimentalFeatures={{
            lazyLoading: true,
          }}
          slots={{
            toolbar: TCClosure({
              hasUnsavedRows,
              isSaving,
              saveChanges,
              discardChanges,
            }),
          }}
          processRowUpdate={(
            newRow: GridValidRowModel,
            oldRow: GridValidRowModel
          ) => {
            return processRowUpdate(newRow, oldRow)
          }}
          getRowClassName={({ id }) => {
            const unsavedRow = unsavedChangesRef.current.unsavedRows[id]
            if (unsavedRow) {
              return 'row--edited'
            }
            return ''
          }}
        />
      </Box>
    </Box>
  )
}
